import t from "@/lang/lang";
import {
  usePestcontrolPestDeleteMutation,
  usePestcontrolPestGroupDeleteMutation,
  usePestcontrolPestGroupListQuery,
  usePestcontrolPestGroupPutMutation,
  usePestcontrolPestListQuery,
  usePestcontrolPestPutMutation,
} from "@/services/backend/pestcontrol/api/pest-api";
import {
  Pest,
  PestGroup,
  PestGroupSnapshot,
  PestKind,
} from "@/services/backend/pestcontrol/types/pest";
import {
  filterGroups,
  filterPests,
  formatPestKind,
  formatSynonyms,
  groupToSnapshot,
  newGroupDraft,
  newPestDraft,
  normalizePest,
  orderGroups,
  orderPests,
  parseSynonyms,
  PestGroupFilterValue,
  PestGroupOrderBy,
  PestOrderBy,
} from "@/services/backend/pestcontrol/lib/pest";
import { PestcontrolField } from "@/services/backend/pestcontrol/components/pestcontrol-field";
import { PestcontrolRowActions } from "@/services/backend/pestcontrol/components/row-actions";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { is404 } from "@/shared/components/errors/parse-r-t-k-query-error";
import {
  DataTable,
  DataTableBrowserPagination,
  DataTableContainer,
  DataTableFilters,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import { Filter, FilterMode } from "@/shared/lib/filter/filter";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { ColumnDef } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

export function PestGroupsTable({ companyId }: { companyId: string }) {
  const request = useMemo(
    () => ({ company: { active: true, values: [companyId] }, limit: 500 }),
    [companyId],
  );
  const { data, error, isLoading } = usePestcontrolPestGroupListQuery(request);
  const [deleteGroup, deleteGroupState] =
    usePestcontrolPestGroupDeleteMutation();
  const [editingGroup, setEditingGroup] = useState<PestGroup | null>(null);
  const [search, setSearch] = useState<Filter<string>>({
    active: false,
    mode: FilterMode.Like,
    values: [],
  });
  const [orderBy, setOrderBy] = useState<OrdrBy<PestGroupOrderBy>[]>([
    { field: "name" },
  ]);

  const groups = useMemo(
    () => orderGroups(filterGroups(data?.items ?? [], search), orderBy),
    [data?.items, orderBy, search],
  );
  const columns = usePestGroupColumns({
    onEdit: setEditingGroup,
    onDelete: (group) => deleteGroup({ id: group.id }).unwrap(),
  });
  const table = useDataTableController(columns, groups);

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">{t("Schädlingsgruppen")}</h2>
      </div>
      {error && !is404(error) && <RTKQueryErrorAlert error={error} />}
      <DataTableContainer className={isLoading ? "animate-pulse" : undefined}>
        <DataTableFilters className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <SearchFilter
              className="h-9 w-[230px]"
              filter={search}
              onFilterChange={setSearch}
              placeholder={t("Nach Name suchen ...")}
            />
            <OrderBySelect<PestGroupOrderBy>
              orderBy={orderBy}
              onOrderByChange={setOrderBy}
              fields={[
                { label: t("Name"), value: "name" },
                { label: t("Beschreibung"), value: "description" },
              ]}
            />
          </div>
          <PestGroupDialog
            companyId={companyId}
            trigger={
              <Button>
                <Plus className="size-4" />
                {t("Gruppe anlegen")}
              </Button>
            }
          />
        </DataTableFilters>
        <DataTable columns={columns} table={table} />
        <DataTableBrowserPagination table={table} />
        {deleteGroupState.error && (
          <RTKQueryErrorAlert error={deleteGroupState.error} />
        )}
        {editingGroup && (
          <PestGroupDialog
            companyId={companyId}
            group={editingGroup}
            open={!!editingGroup}
            onOpenChange={(open) => {
              if (!open) setEditingGroup(null);
            }}
          />
        )}
      </DataTableContainer>
    </section>
  );
}

export function PestsTable({ companyId }: { companyId: string }) {
  const [kind, setKind] = useState<PestKind | "all">("all");
  const [groupFilter, setGroupFilter] = useState<PestGroupFilterValue>("all");
  const [search, setSearch] = useState<Filter<string>>({
    active: false,
    mode: FilterMode.Like,
    values: [],
  });
  const [orderBy, setOrderBy] = useState<OrdrBy<PestOrderBy>[]>([
    { field: "kind" },
    { field: "name" },
  ]);
  const groupsRequest = useMemo(
    () => ({ company: { active: true, values: [companyId] }, limit: 500 }),
    [companyId],
  );
  const pestRequest = useMemo(
    () => ({
      company: { active: true, values: [companyId] },
      kind: kind === "all" ? undefined : kind,
      noGroup: groupFilter === "none" ? true : undefined,
      q: search.active ? search.values?.[0] : undefined,
      limit: 500,
    }),
    [companyId, groupFilter, kind, search],
  );

  const { data: groupsData } = usePestcontrolPestGroupListQuery(groupsRequest);
  const { data, error, isLoading } = usePestcontrolPestListQuery(pestRequest);
  const [deletePest, deletePestState] = usePestcontrolPestDeleteMutation();
  const [editingPest, setEditingPest] = useState<Pest | null>(null);
  const groups = groupsData?.items ?? [];
  const pests = useMemo(
    () =>
      orderPests(
        filterPests((data?.items ?? []).map(normalizePest), groupFilter),
        orderBy,
      ),
    [data?.items, groupFilter, orderBy],
  );
  const columns = usePestColumns({
    onEdit: setEditingPest,
    onDelete: (pest) => deletePest({ id: pest.id }).unwrap(),
  });
  const table = useDataTableController(columns, pests);

  return (
    <section className="space-y-3">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">
          {t("Schädlingskategorien und -indikatoren")}
        </h2>
      </div>
      {error && !is404(error) && <RTKQueryErrorAlert error={error} />}
      <DataTableContainer className={isLoading ? "animate-pulse" : undefined}>
        <DataTableFilters className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <SearchFilter
              className="h-9 w-[230px]"
              filter={search}
              onFilterChange={setSearch}
              placeholder={t("Nach Name suchen ...")}
            />
            <PestKindFilter value={kind} onChange={setKind} />
            <PestGroupFilter
              groups={groups}
              value={groupFilter}
              onChange={setGroupFilter}
            />
            <OrderBySelect<PestOrderBy>
              orderBy={orderBy}
              onOrderByChange={setOrderBy}
              fields={[
                { label: t("Art"), value: "kind" },
                { label: t("Name"), value: "name" },
                { label: t("Gruppe"), value: "group" },
                { label: t("Fachbegriff"), value: "technicalTerm" },
              ]}
            />
          </div>
          <PestDialog
            companyId={companyId}
            groups={groups}
            initialKind={PestKind.Category}
            trigger={
              <Button>
                <Plus className="size-4" />
                {t("Schädling anlegen")}
              </Button>
            }
          />
        </DataTableFilters>
        <DataTable columns={columns} table={table} />
        <DataTableBrowserPagination table={table} />
        {deletePestState.error && (
          <RTKQueryErrorAlert error={deletePestState.error} />
        )}
        {editingPest && (
          <PestDialog
            companyId={companyId}
            groups={groups}
            pest={editingPest}
            initialKind={editingPest.kind}
            open={!!editingPest}
            onOpenChange={(open) => {
              if (!open) setEditingPest(null);
            }}
          />
        )}
      </DataTableContainer>
    </section>
  );
}

function PestKindFilter({
  value,
  onChange,
}: {
  value: PestKind | "all";
  onChange: (value: PestKind | "all") => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) =>
        onChange(next === "all" ? "all" : (next as PestKind))
      }
    >
      <SelectTrigger className="h-9 w-[190px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("Alle Arten")}</SelectItem>
        <SelectItem value={PestKind.Category}>
          {formatPestKind(PestKind.Category)}
        </SelectItem>
        <SelectItem value={PestKind.Indicator}>
          {formatPestKind(PestKind.Indicator)}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function PestGroupFilter({
  groups,
  value,
  onChange,
}: {
  groups: PestGroup[];
  value: PestGroupFilterValue;
  onChange: (value: PestGroupFilterValue) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => onChange(next as PestGroupFilterValue)}
    >
      <SelectTrigger className="h-9 w-[190px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("Alle Gruppen")}</SelectItem>
        <SelectItem value="none">{t("Ohne Gruppe")}</SelectItem>
        {groups.map((group) => (
          <SelectItem key={group.id} value={group.id}>
            {group.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function PestGroupDialog({
  companyId,
  group,
  trigger,
  open,
  onOpenChange,
}: {
  companyId: string;
  group?: PestGroup;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [putGroup, putState] = usePestcontrolPestGroupPutMutation();
  const [draft, setDraft] = useState<PestGroup>(
    group ? { ...group } : newGroupDraft(companyId),
  );
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const isNew = !group;
  const canSave = draft.name.trim().length > 0 && !putState.isLoading;
  const resetPutState = putState.reset;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetPutState();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetPutState]);

  useEffect(() => {
    if (!isOpen || group) {
      return;
    }
    setDraft(newGroupDraft(companyId));
  }, [companyId, group, isOpen]);

  const save = async () => {
    if (!canSave) return;
    try {
      await putGroup({
        companyId,
        group: {
          ...draft,
          companyId,
          name: draft.name.trim(),
          description: draft.description?.trim() ?? "",
        },
      }).unwrap();
    } catch {
      return;
    }
    setOpen(false);
    if (!group) {
      setDraft(newGroupDraft(companyId));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t("Gruppe anlegen") : t("Gruppe bearbeiten")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <PestcontrolField label={t("Name")}>
            <Input
              value={draft.name}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  name: event.target.value,
                })
              }
            />
          </PestcontrolField>
          <PestcontrolField className="md:col-span-2" label={t("Beschreibung")}>
            <Textarea
              value={draft.description ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, description: event.target.value })
              }
            />
          </PestcontrolField>
        </div>
        {putState.error && <RTKQueryErrorAlert error={putState.error} />}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            {t("Abbrechen")}
          </Button>
          <Button type="button" onClick={save} disabled={!canSave}>
            {t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PestDialog({
  companyId,
  groups,
  pest,
  initialKind,
  trigger,
  open,
  onOpenChange,
}: {
  companyId: string;
  groups: PestGroup[];
  pest?: Pest;
  initialKind: PestKind;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [putPest, putState] = usePestcontrolPestPutMutation();
  const [draft, setDraft] = useState<Pest>(
    pest
      ? normalizePest(pest)
      : newPestDraft(companyId, initialKind, groups[0]),
  );
  const [synonymsInput, setSynonymsInput] = useState(draft.synonyms.join(", "));
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const isNew = !pest;
  const canSave = draft.name.trim().length > 0 && !putState.isLoading;
  const resetPutState = putState.reset;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetPutState();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetPutState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (pest) {
      const next = normalizePest(pest);
      setDraft(next);
      setSynonymsInput(next.synonyms.join(", "));
      return;
    }
    const next = newPestDraft(companyId, initialKind);
    setDraft(next);
    setSynonymsInput("");
  }, [companyId, groups, initialKind, isOpen, pest]);

  const save = async () => {
    if (!canSave) return;
    try {
      await putPest({
        companyId,
        pest: {
          ...draft,
          companyId,
          group: draft.group,
          name: draft.name.trim(),
          technicalTerm: draft.technicalTerm?.trim() ?? "",
          abbreviation: draft.abbreviation?.trim() ?? "",
          description: draft.description?.trim() ?? "",
          synonyms: parseSynonyms(synonymsInput),
        },
      }).unwrap();
    } catch {
      return;
    }
    setOpen(false);
    if (!pest) {
      const next = newPestDraft(companyId, initialKind);
      setDraft(next);
      setSynonymsInput("");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {isNew ? t("Schädling anlegen") : t("Schädling bearbeiten")}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 md:grid-cols-2">
          <PestcontrolField label={t("Art")}>
            <Select
              value={draft.kind}
              disabled={!isNew}
              onValueChange={(value) => {
                const nextKind = value as PestKind;
                setDraft({
                  ...draft,
                  kind: nextKind,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PestKind.Category}>
                  {formatPestKind(PestKind.Category)}
                </SelectItem>
                <SelectItem value={PestKind.Indicator}>
                  {formatPestKind(PestKind.Indicator)}
                </SelectItem>
              </SelectContent>
            </Select>
          </PestcontrolField>
          <PestcontrolField label={t("Name")}>
            <Input
              value={draft.name}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  name: event.target.value,
                })
              }
            />
          </PestcontrolField>
          <PestcontrolField label={t("Gruppe")}>
            <GroupSelect
              groups={groups}
              value={draft.group?.sourceId ?? "none"}
              onChange={(group) => setDraft({ ...draft, group })}
            />
          </PestcontrolField>
          <PestcontrolField label={t("Fachbegriff")}>
            <Input
              value={draft.technicalTerm ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, technicalTerm: event.target.value })
              }
            />
          </PestcontrolField>
          <PestcontrolField label={t("Abkürzung")}>
            <Input
              value={draft.abbreviation ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, abbreviation: event.target.value })
              }
            />
          </PestcontrolField>
          <PestcontrolField className="md:col-span-2" label={t("Synonyme")}>
            <Input
              value={synonymsInput}
              onChange={(event) => setSynonymsInput(event.target.value)}
            />
          </PestcontrolField>
          <PestcontrolField className="md:col-span-2" label={t("Beschreibung")}>
            <Textarea
              value={draft.description ?? ""}
              onChange={(event) =>
                setDraft({ ...draft, description: event.target.value })
              }
            />
          </PestcontrolField>
        </div>
        {putState.error && <RTKQueryErrorAlert error={putState.error} />}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            {t("Abbrechen")}
          </Button>
          <Button type="button" onClick={save} disabled={!canSave}>
            {t("Speichern")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupSelect({
  groups,
  value,
  onChange,
}: {
  groups: PestGroup[];
  value: string;
  onChange: (value: PestGroupSnapshot | undefined) => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(id) => {
        if (id === "none") {
          onChange(undefined);
          return;
        }
        const group = groups.find((item) => item.id === id);
        if (group) {
          const snapshot = groupToSnapshot(group);
          if (snapshot) {
            onChange(snapshot);
          }
        }
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">{t("Keine Gruppe")}</SelectItem>
        {groups.map((group) => (
          <SelectItem key={group.id} value={group.id}>
            {group.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function usePestGroupColumns({
  onEdit,
  onDelete,
}: {
  onEdit: (group: PestGroup) => void;
  onDelete: (group: PestGroup) => Promise<unknown>;
}): ColumnDef<PestGroup>[] {
  return useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: t("Beschreibung"),
        cell: ({ row }) => row.original.description || "-",
      },
      {
        id: "actions",
        header: t("Aktion"),
        cell: ({ row }) => (
          <PestcontrolRowActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
          />
        ),
      },
    ],
    [onDelete, onEdit],
  );
}

function usePestColumns({
  onEdit,
  onDelete,
}: {
  onEdit: (pest: Pest) => void;
  onDelete: (pest: Pest) => Promise<unknown>;
}): ColumnDef<Pest>[] {
  return useMemo(
    () => [
      {
        header: t("Art"),
        cell: ({ row }) => formatPestKind(row.original.kind),
      },
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: t("Gruppe"),
        cell: ({ row }) => row.original.group?.name ?? "-",
      },
      {
        header: t("Fachbegriff"),
        cell: ({ row }) => row.original.technicalTerm || "-",
      },
      {
        header: t("Abkürzung"),
        cell: ({ row }) => row.original.abbreviation || "-",
      },
      {
        header: t("Beschreibung"),
        cell: ({ row }) => row.original.description || "-",
      },
      {
        header: t("Synonyme"),
        cell: ({ row }) => formatSynonyms(row.original.synonyms),
      },
      {
        id: "actions",
        header: t("Aktion"),
        cell: ({ row }) => (
          <PestcontrolRowActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
          />
        ),
      },
    ],
    [onDelete, onEdit],
  );
}
